import { formOptions } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ConfirmModal from '~/components/ConfirmModal';
import DataTable from '~/components/DataTable';
import { LevelCustomCopyPost } from '~/components/LevelCustomCopyPost';
import { useScopes } from '~/components/Providers/AuthProvider';
import { UserTags } from '~/partials/Tags';
import TooltipButton from '~/partials/Tooltip';
import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';
import { ScrollArea } from '~/primitives/scroll-area';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { camelToSpaces, cn, ModalProps } from '~/util';
import {
    levelCustomCopyFormInput,
    levelCustomCopyFormSchema,
} from '~/util/forms/level';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { useAppForm } from '~/util/hooks/useAppForm';
import { useList } from '~/util/hooks/useList';
import {
    createCustomCopyMutation,
    deleteCustomCopyMutation,
    updateCustomCopyMutation,
} from '~/util/mutations/staff/levels';
import { levelCustomCopiesQuery } from '~/util/queries/public/levels';
import {
    LevelCustomCopy,
    levelCustomCopyStatuses,
    levelCustomCopyTypes,
} from '~/util/types/levels';

const Route = getRouteApi('/list/$levelid');

export const LevelCustomCopies = () => {
    const list = useList();

    const { levelid } = Route.useParams();

    const { level_id } = Route.useLoaderData({
        select: ({ level_id }) => ({ level_id }),
    });

    const [otherIdsOpen, setOtherIdsOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const { data } = useQuery(levelCustomCopiesQuery({ list, levelid }));

    const publishedCustomCopies =
        data?.filter((customCopy) => customCopy.status === 'Published') ?? [];

    const bannedCustomCopies =
        data?.filter((customCopy) => customCopy.status === 'Banned') ?? [];

    publishedCustomCopies.sort(
        (firstCopy, secondCopy) => firstCopy.copy_id - secondCopy.copy_id,
    );
    bannedCustomCopies.sort(
        (firstCopy, secondCopy) => firstCopy.copy_id - secondCopy.copy_id,
    );

    const { hasScope } = useScopes();

    return (
        <div className="flex-1 text-center uppercase p-1 border-r border-secondary-light last:border-none">
            <span className="flex justify-center items-center text-primary clamp-[text-xs-lg-clamp] whitespace-nowrap">
                LEVEL ID
            </span>
            <div className="flex flex-col items-center">
                <span
                    className={cn(
                        'flex justify-center items-center text-secondary-foreground clamp-[text-xl-3xl-clamp] ',
                        (publishedCustomCopies.length > 0 ||
                            bannedCustomCopies.length > 0) &&
                            'leading-none',
                    )}
                >
                    {level_id}
                    {hasScope('level_custom_copies_modify') && (
                        <>
                            <Pencil
                                className="size-6 ml-1 hover:text-secondary-light transition-colors cursor-pointer"
                                onClick={() => setEditModalOpen(true)}
                            />

                            <EditLevelCustomCopyDialog
                                open={editModalOpen}
                                onOpenChange={setEditModalOpen}
                            />
                        </>
                    )}
                </span>
                {publishedCustomCopies.length > 0 ||
                bannedCustomCopies.length > 0 ? (
                    <button
                        className="w-fit font-medium leading-tight border-b-2 text-secondary-light hover:text-secondary-foreground transition-colors cursor-pointer"
                        onClick={() => setOtherIdsOpen(true)}
                    >
                        {publishedCustomCopies.length > 0
                            ? `+ ${publishedCustomCopies.length} custom cop${publishedCustomCopies.length > 1 ? 'ies' : 'y'}`
                            : 'Show banned copies'}
                    </button>
                ) : null}
            </div>
            <Dialog open={otherIdsOpen} onOpenChange={setOtherIdsOpen}>
                <DialogContent className="w-3xl">
                    <DialogHeader>
                        <DialogTitle>Custom Copies</DialogTitle>
                        <DialogDescription>
                            These are custom copies for this level that have
                            already been reviewed by our staff. If you want to
                            use a copy that is not listed as allowed here for a
                            submission, please contact our support on Discord to
                            get it approved.
                        </DialogDescription>
                    </DialogHeader>

                    {publishedCustomCopies &&
                    publishedCustomCopies.length > 0 ? (
                        <>
                            <h3 className="text-lg font-semibold mt-2">
                                Allowed copies
                            </h3>
                            <div className="flex flex-wrap items-center gap-4 py-2">
                                {publishedCustomCopies.map((custom_copy) => (
                                    <LevelCustomCopyPost
                                        key={custom_copy.id}
                                        custom_copy_data={custom_copy}
                                        copy_id={custom_copy.copy_id}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}

                    {bannedCustomCopies && bannedCustomCopies.length > 0 ? (
                        <>
                            <h3 className="text-lg font-semibold mt-4">
                                Banned copies
                            </h3>
                            <div className="flex flex-wrap items-center gap-4 py-2">
                                {bannedCustomCopies.map((custom_copy) => (
                                    <LevelCustomCopyPost
                                        key={custom_copy.id}
                                        custom_copy_data={custom_copy}
                                        copy_id={custom_copy.copy_id}
                                        showStatusWhenAllowed
                                        allowClick={false}
                                    />
                                ))}
                            </div>
                        </>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export const EditLevelCustomCopyDialog = (props: ModalProps) => {
    const [selectedCustomCopy, setSelectedCustomCopy] =
        useState<LevelCustomCopy | null>(null);
    const [isNewSheetOpen, setIsNewSheetOpen] = useState(false);
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const list = useList();

    const { levelid } = Route.useParams();

    const { data, isPending } = useQuery(
        levelCustomCopiesQuery({ list, levelid }),
    );

    const deleteMutation = useApiMutation(deleteCustomCopyMutation());

    const columns: ColumnDef<LevelCustomCopy>[] = useMemo(
        () => [
            {
                accessorKey: 'id_type',
                header: 'Type',
                cell: ({
                    row: {
                        original: { id_type },
                    },
                }) => camelToSpaces(id_type).toUpperCase(),
            },
            {
                accessorKey: 'copy_id',
                header: 'ID',
                cell: ({
                    row: {
                        original: { copy_id },
                    },
                }) => copy_id,
            },
            {
                accessorKey: 'description',
                header: 'Description',
                cell: ({
                    row: {
                        original: { description },
                    },
                }) => description || 'N/A',
            },
            {
                accessorKey: 'added_by.global_name',
                header: 'Added by',
                cell: ({
                    row: {
                        original: { added_by },
                    },
                }) => <UserTags showFullName highContrast item={added_by} />,
            },
            {
                accessorKey: 'status',
                header: 'Status',
                cell: ({
                    row: {
                        original: { status },
                    },
                }) => status,
            },
            {
                id: 'actions',
                cell: ({ row: { original: custom_copy } }) => {
                    return (
                        <div className="flex items-center gap-1">
                            <TooltipButton text="Edit Custom Copy" side="top">
                                <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => {
                                        setSelectedCustomCopy(custom_copy);
                                        setIsEditSheetOpen(true);
                                        e.stopPropagation();
                                    }}
                                >
                                    <Pencil className="h-5 w-5 m-auto" />
                                </Button>
                            </TooltipButton>
                            <TooltipButton text="Delete Custom Copy" side="top">
                                <Button
                                    variant="destructive"
                                    className="h-8 w-8 p-0"
                                    onClick={(event) => {
                                        setSelectedCustomCopy(custom_copy);
                                        setIsConfirmModalOpen(true);
                                        event.stopPropagation();
                                    }}
                                >
                                    <Trash className="h-5 w-5 m-auto" />
                                </Button>
                            </TooltipButton>
                        </div>
                    );
                },
            },
        ],
        [],
    );

    return (
        <Dialog {...props}>
            <DialogContent disableDialogScroll>
                <DialogHeader className="pl-3">
                    <DialogTitle>Other IDs</DialogTitle>
                    <DialogDescription>
                        Other IDs for this level can be detailed here (Such as
                        bugfixes, LDMs, etc...). IDs added here with the
                        &quot;Published&quot; status will be publicly visible on
                        the level page. Other statuses (&quot;Allowed&quot; /
                        &quot;Banned&quot;) can be used to specify if a specific
                        ID has already been allowed or banned (For example,
                        through a support thread on the discord server), and
                        will only be visible to other reviewers.
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="flex-1 min-h-0 mr-1 pr-3">
                    <Button
                        className="my-4"
                        onClick={() => setIsNewSheetOpen(true)}
                    >
                        <Plus /> Add ID
                    </Button>
                    <DataTable
                        isLoading={isPending}
                        columns={columns}
                        data={data ?? []}
                    />
                </ScrollArea>
                <ConfirmModal
                    open={isConfirmModalOpen}
                    onOpenChange={setIsConfirmModalOpen}
                    title="Delete ID"
                    text="Are you sure you want to delete this ID? This action cannot be undone."
                    onConfirm={() => {
                        if (!selectedCustomCopy) return;
                        deleteMutation.mutate({
                            copy_id: selectedCustomCopy.id,
                            levelid,
                            list,
                        });
                    }}
                />
                <NewCustomCopySheet
                    open={isNewSheetOpen}
                    onOpenChange={setIsNewSheetOpen}
                />
                <EditCustomCopySheet
                    copy={selectedCustomCopy!}
                    open={isEditSheetOpen}
                    onOpenChange={setIsEditSheetOpen}
                />
            </DialogContent>
        </Dialog>
    );
};

export const NewCustomCopySheet: React.FC<ModalProps> = (modalProps) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const mutation = useApiMutation({
        ...createCustomCopyMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'add-ldm-staff',
            defaultValues: {} as levelCustomCopyFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelCustomCopyFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({ data: value, list, levelid });
            },
        }),
    );

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Create ID</SheetTitle>
                        <SheetDescription>
                            Register a new custom copy ID for this level.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="add-ldm-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="id_type">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelCustomCopyTypes}
                                        optionsDisplay={levelCustomCopyTypes.map(
                                            (type) => camelToSpaces(type),
                                        )}
                                        label="ID Type"
                                        required
                                        placeholder="Select ID Type"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="copy_id">
                                {(field) => (
                                    <field.NumberField
                                        label="ID"
                                        placeholder="ID of the copy in game"
                                        required
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="status">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelCustomCopyStatuses}
                                        label="Status"
                                        required
                                        placeholder="Select Status"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="description">
                                {(field) => (
                                    <field.TextField
                                        label="Description"
                                        placeholder="Notes about this copy"
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Create ID"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};

export const EditCustomCopySheet: React.FC<
    { copy: LevelCustomCopy } & ModalProps
> = ({ copy: ldm, ...modalProps }) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const mutation = useApiMutation({
        ...updateCustomCopyMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'edit-ldm-staff',
            defaultValues: ldm as levelCustomCopyFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelCustomCopyFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({
                    data: value,
                    list,
                    levelid,
                    copy_id: ldm.id,
                });
            },
        }),
    );

    useEffect(() => {
        form.reset(ldm);
    }, [ldm, form]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit ID</SheetTitle>
                        <SheetDescription>
                            Update this custom copy ID&apos;s details.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="edit-ldm-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="id_type">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelCustomCopyTypes}
                                        optionsDisplay={levelCustomCopyTypes.map(
                                            (type) => camelToSpaces(type),
                                        )}
                                        label="ID Type"
                                        required
                                        placeholder="Select ID Type"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="copy_id">
                                {(field) => (
                                    <field.NumberField
                                        label="ID"
                                        placeholder="ID of the copy in game"
                                        required
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="status">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelCustomCopyStatuses}
                                        label="Status"
                                        required
                                        placeholder="Select Status"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="description">
                                {(field) => (
                                    <field.TextField
                                        label="Description"
                                        placeholder="Notes about this copy"
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Edit ID"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
